import { useState, useEffect, useMemo } from 'react'
import sidebarData from '@/docs/_sidebar.json'
import type { DocsNavigation, DocsPageMeta } from '@/types/docs'

// Import all markdown files from the docs directory
const markdownModules = import.meta.glob('/src/docs/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: false
})

interface UseDocsResult {
  content: string | null
  isLoading: boolean
  error: string | null
  pageTitle: string
  section: string
  prevPage: { title: string; path: string } | null
  nextPage: { title: string; path: string } | null
}

/**
 * Hook to load and manage documentation content
 */
export function useDocs(path: string): UseDocsResult {
  const [content, setContent] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const navigation = sidebarData as DocsNavigation

  // Find current page info
  const pageInfo = useMemo(() => {
    let pageTitle = ''
    let section = ''
    let prevPage: { title: string; path: string } | null = null
    let nextPage: { title: string; path: string } | null = null

    // Flatten all items to find prev/next
    const allItems: { title: string; path: string; section: string }[] = []
    navigation.sections.forEach((s) => {
      s.items.forEach((item) => {
        allItems.push({ ...item, section: s.title })
      })
    })

    const currentIndex = allItems.findIndex((item) => item.path === path)
    if (currentIndex !== -1) {
      pageTitle = allItems[currentIndex].title
      section = allItems[currentIndex].section
      if (currentIndex > 0) {
        prevPage = allItems[currentIndex - 1]
      }
      if (currentIndex < allItems.length - 1) {
        nextPage = allItems[currentIndex + 1]
      }
    }

    return { pageTitle, section, prevPage, nextPage }
  }, [path, navigation])

  // Load markdown content
  useEffect(() => {
    let cancelled = false

    const loadContent = async () => {
      setIsLoading(true)
      setError(null)

      const modulePath = `/src/docs/${path}.md`
      const loader = markdownModules[modulePath]

      if (!loader) {
        setError(`Documentation page not found: ${path}`)
        setContent(null)
        setIsLoading(false)
        return
      }

      try {
        const content = await loader() as string
        if (!cancelled) {
          setContent(content)
          setIsLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(`Failed to load documentation: ${err instanceof Error ? err.message : 'Unknown error'}`)
          setContent(null)
          setIsLoading(false)
        }
      }
    }

    loadContent()

    return () => {
      cancelled = true
    }
  }, [path])

  return {
    content,
    isLoading,
    error,
    ...pageInfo,
  }
}

/**
 * Get all documentation pages for search indexing
 * This loads all markdown content eagerly for search
 */
export async function getAllDocsForSearch(): Promise<DocsPageMeta[]> {
  const navigation = sidebarData as DocsNavigation
  const pages: DocsPageMeta[] = []

  for (const section of navigation.sections) {
    for (const item of section.items) {
      const modulePath = `/src/docs/${item.path}.md`
      const loader = markdownModules[modulePath]

      if (loader) {
        try {
          const content = await loader() as string
          pages.push({
            title: item.title,
            section: section.title,
            path: item.path,
            content,
          })
        } catch {
          // Skip pages that fail to load
          console.warn(`Failed to load ${item.path} for search index`)
        }
      }
    }
  }

  return pages
}

/**
 * Extract title from markdown content (first H1)
 */
export function extractTitleFromMarkdown(content: string): string | null {
  const match = content.match(/^#\s+(.+)$/m)
  return match ? match[1].trim() : null
}
